import { useState, useMemo, useEffect } from 'react'
import { ChevronRight, ChevronDown, File, Folder, FolderOpen, Plus, Search, Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { StatusBadge } from '@/components/status-badge'
import type { IngestionJob, VaultListEntry } from './vault-api'

// M8.1: Demo-Seed-Dateien (Onboarding) bekommen im Tree ein "Beispiel"-Badge.
const DEMO_PATH_PREFIX = '_Beispiele/'

interface TreeNode {
  name: string
  path: string
  type: 'file' | 'directory'
  children: TreeNode[]
  size_bytes?: number
  updated_at?: string
  tags?: string[]
}

function sortNodes(nodes: TreeNode[]): void {
  nodes.sort((a, b) => {
    // Ordner immer vor Files
    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1
    // Ordner alphabetisch
    if (a.type === 'directory') return a.name.localeCompare(b.name, 'de')
    // Files: neueste zuerst (B1.6.4), Fallback alphabetisch wenn Timestamps gleich
    const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0
    const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0
    if (ta !== tb) return tb - ta
    return a.name.localeCompare(b.name, 'de')
  })
  for (const n of nodes) {
    if (n.children.length > 0) sortNodes(n.children)
  }
}

function buildTree(entries: VaultListEntry[]): TreeNode[] {
  const root: TreeNode[] = []
  const dirMap = new Map<string, TreeNode>()

  // Backend liefert flache Datei-Liste; Verzeichnisse werden hier
  // beim Walken on-the-fly aus den Pfad-Segmenten erzeugt.
  for (const entry of entries) {
    const parts = entry.path.split('/')
    let current = root

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i] ?? ''
      const pathSoFar = parts.slice(0, i + 1).join('/')
      const isLeaf = i === parts.length - 1

      if (isLeaf) {
        const node: TreeNode = {
          name: part,
          path: entry.path,
          type: entry.type,
          children: [],
          size_bytes: entry.size_bytes,
          updated_at: entry.updated_at,
          tags: entry.tags,
        }
        current.push(node)
        if (entry.type === 'directory') {
          dirMap.set(pathSoFar, node)
        }
      } else {
        let dir = dirMap.get(pathSoFar)
        if (!dir) {
          dir = {
            name: part,
            path: pathSoFar,
            type: 'directory',
            children: [],
          }
          current.push(dir)
          dirMap.set(pathSoFar, dir)
        }
        current = dir.children
      }
    }
  }

  sortNodes(root)

  // B1.7.4 — Diagnose (nur Dev-Build): zeigt die Reihenfolge in _Research/Dossiers/
  // damit man im Browser-Konsolen-Log direkt sieht, ob updated_at DESC greift.
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    const dossiers = root
      .find((r) => r.name === '_Research')
      ?.children.find((c) => c.name === 'Dossiers')?.children
    if (dossiers && dossiers.length > 0) {

      console.debug(
        '[vault-tree] _Research/Dossiers/ order:',
        dossiers.slice(0, 8).map((f) => ({ name: f.name, updated_at: f.updated_at })),
      )
    }
  }

  return root
}

interface TreeNodeProps {
  node: TreeNode
  depth: number
  selectedPath: string | null
  onSelect: (path: string) => void
  expandedDirs: Set<string>
  onToggleDir: (path: string) => void
  ingestionJobsByPath?: Map<string, IngestionJob>
}

function TreeNodeRow({
  node,
  depth,
  selectedPath,
  onSelect,
  expandedDirs,
  onToggleDir,
  ingestionJobsByPath,
}: TreeNodeProps) {
  const isExpanded = expandedDirs.has(node.path)
  const isSelected = selectedPath === node.path
  const isDir = node.type === 'directory'
  const ingestionJob = !isDir ? ingestionJobsByPath?.get(node.path) : undefined

  const handleClick = () => {
    if (isDir) {
      onToggleDir(node.path)
    } else {
      onSelect(node.path)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          'flex w-full items-center gap-1.5 rounded-md px-2 py-1 min-h-11 min-[761px]:min-h-9 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isSelected && !isDir
            ? 'bg-accent text-accent-foreground font-medium'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        title={node.path}
      >
        {isDir ? (
          <>
            <span className="shrink-0 text-muted-foreground/60">
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </span>
            {isExpanded ? (
              <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
          </>
        ) : (
          <>
            <span className="w-3 shrink-0" />
            <File className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
          </>
        )}
        <span className="min-w-0 truncate">{node.name}</span>
        {!isDir && node.path.startsWith(DEMO_PATH_PREFIX) && (
          <StatusBadge variant="info" className="ml-1 shrink-0 px-1.5 py-0 text-[10px]">
            Beispiel
          </StatusBadge>
        )}
        {ingestionJob && (
          <span
            className={cn(
              'ml-auto h-2 w-2 shrink-0 rounded-full',
              ingestionJob.status === 'completed' && 'bg-emerald-500',
              ingestionJob.status === 'completed_with_warnings' && 'bg-amber-500',
              ingestionJob.status === 'failed' && 'bg-destructive',
              ingestionJob.status === 'cancelled' && 'bg-muted-foreground/50',
              ingestionJob.status === 'processing' && 'animate-pulse bg-blue-500',
              ingestionJob.status === 'pending' && 'bg-muted-foreground/50',
            )}
            title={ingestionStatusTitle(ingestionJob)}
          />
        )}
      </button>

      {isDir && isExpanded && (
        <>
          {node.children.map((child) => (
            <TreeNodeRow
              key={child.path}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
              expandedDirs={expandedDirs}
              onToggleDir={onToggleDir}
              ingestionJobsByPath={ingestionJobsByPath}
            />
          ))}
          {node.children.length === 0 && (
            <p
              className="py-1 text-xs text-muted-foreground/50 italic"
              style={{ paddingLeft: `${8 + (depth + 1) * 16 + 20}px` }}
            >
              leer
            </p>
          )}
        </>
      )}
    </>
  )
}

interface VaultTreeProps {
  entries: VaultListEntry[]
  selectedPath: string | null
  onSelectFile: (path: string) => void
  // F05a2: Die Launcher-Callbacks erhalten das MouseEvent des geklickten
  // Buttons, damit VaultPage den tatsächlichen Auslöser für die Fokus-Rückgabe
  // nach dem Dialog-Schliessen erfassen kann.
  onNewFile: (event: React.MouseEvent<HTMLButtonElement>) => void
  onBulkUpload?: (event: React.MouseEvent<HTMLButtonElement>) => void
  isLoading?: boolean
  ingestionJobsByPath?: Map<string, IngestionJob>
}

export function VaultTree({
  entries,
  selectedPath,
  onSelectFile,
  onNewFile,
  onBulkUpload,
  isLoading,
  ingestionJobsByPath,
}: VaultTreeProps) {
  const [search, setSearch] = useState('')
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set())

  // Auto-expand ancestor folders of the selected file
  useEffect(() => {
    if (!selectedPath) return
    const parts = selectedPath.split('/')
    if (parts.length <= 1) return
    setExpandedDirs((prev) => {
      const next = new Set(prev)
      let acc = ''
      for (let i = 0; i < parts.length - 1; i++) {
        acc = acc ? `${acc}/${parts[i]}` : (parts[i] ?? '')
        if (acc) next.add(acc)
      }
      return next
    })
  }, [selectedPath])

  const filteredEntries = useMemo(() => {
    if (!search.trim()) return entries
    const q = search.toLowerCase()
    return entries.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.tags?.some((t) => t.toLowerCase().includes(q)),
    )
  }, [entries, search])

  const tree = useMemo(() => buildTree(filteredEntries), [filteredEntries])

  const toggleDir = (path: string) => {
    setExpandedDirs((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="shrink-0 border-b px-3 py-2">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Quellen
          </span>
          <div className="flex items-center gap-1">
            {onBulkUpload && (
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground"
                onClick={onBulkUpload}
                title="Bulk-Upload"
                aria-label="Bulk-Upload"
              >
                <Upload className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground"
              onClick={onNewFile}
              title="Neue Datei"
              aria-label="Neue Datei"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen…"
            className="pl-6 text-xs"
            aria-label="Quellen durchsuchen"
          />
        </div>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto py-1">
        {isLoading ? (
          <div className="space-y-1 px-2 py-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-6 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : tree.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <Folder className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-xs text-muted-foreground">
              {search ? 'Keine Treffer' : 'Noch keine Quellen'}
            </p>
            {!search && (
              <Button variant="outline" size="sm" className="text-xs" onClick={onNewFile}>
                Erste Notiz erstellen
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-px px-1">
            {tree.map((node) => (
              <TreeNodeRow
                key={node.path}
                node={node}
                depth={0}
                selectedPath={selectedPath}
                onSelect={onSelectFile}
                expandedDirs={expandedDirs}
                onToggleDir={toggleDir}
                ingestionJobsByPath={ingestionJobsByPath}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ingestionStatusTitle(job: IngestionJob): string {
  const result = job.result && typeof job.result === 'object'
    ? job.result as { summary?: unknown; tags?: unknown }
    : null
  const summary = typeof result?.summary === 'string' ? `\n${result.summary}` : ''
  const tags = Array.isArray(result?.tags) ? `\nTags: ${result.tags.join(', ')}` : ''
  const lastError = job.last_error ?? job.error ?? null
  if (job.status === 'completed') return `Verarbeitung abgeschlossen${summary}${tags}`
  if (job.status === 'completed_with_warnings') return `Verarbeitung mit Hinweisen${lastError ? `: ${lastError}` : ''}${summary}${tags}`
  if (job.status === 'failed') return `Analyse fehlgeschlagen: ${lastError ?? 'unbekannt'}`
  if (job.status === 'cancelled') return 'Analyse abgebrochen'
  if (job.status === 'processing') return 'Analyse läuft'
  return 'Wird analysiert'
}
